import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ProductService, SubscriptionService, UsersService } from '@suvo-bi-users';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { IItemsTableOptions } from '../../../../../data-source/data-source-items/interfaces/items-table-options.interface';
import { IUIDataSourceItem } from '../../../../../data-source/data-source-items/interfaces/ui-data-source-item';
import { FiltersChannel } from '../../../../../filters/models/filters-channel.model';
import { TileItemDrawerService } from '../../../../services/tile-item-drawer.service';
import { ITableTileDefinition } from '../../../../interfaces/tiles/definitions/table-tile-definition.interface';

@Component({
  standalone: false,
  selector: 'suvo-bi-table-tile',
  templateUrl: './table-tile.component.html',
  styleUrls: ['./table-tile.component.scss'],
})
export class TableTileComponent {
  @Input() tileInstance: ITableTileDefinition;
  @Input() filtersChannel: FiltersChannel;
  @Input() editMode: boolean = false;

  showExportButton = true;
  exportDisabled = false;

  dataSourceAlias: string;
  options: IItemsTableOptions;

  tableAlias = '';

  allowed = false;
  hasRequiredConfig = false;

  isTrialing = false;
  hasRestrictions = false;

  currentTileInstanceString;

  private $unsubscribe: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly router: Router,
    private readonly productService: ProductService,
    private readonly subscriptionService: SubscriptionService,
    private readonly usersService: UsersService,
    private readonly tileItemDrawerService: TileItemDrawerService,
  ) {}

  ngOnChanges(changes) {
    if (changes.tileInstance) {
      let newDefinitionString = JSON.stringify(changes.tileInstance);
      if (this.currentTileInstanceString != newDefinitionString) {
        this.initialise();

        this.currentTileInstanceString = newDefinitionString;
      }
      return;
    }

    if (changes.editMode && this.tileInstance) {
      this.initialise();
    }
  }

  initialise() {
    let userId = this.usersService?.userSubject?.value?._id;

    //TODO : Potentially add tile specific alias
    this.tableAlias = `${userId}_${this.router.url.toString()}_table-tile`;

    this.dataSourceAlias = this.tileInstance.recipe?.dataSource;
    const hasDataSource = !!this.tileInstance?.recipe?.dataSource;
    const hasColumns = (this.tileInstance?.showColumns?.length ?? 0) > 0;
    this.hasRequiredConfig = hasDataSource && hasColumns;

    const itemRoutePrefixes = /* this.tileInstance.itemRoutePrefixes || */ [
      '/',
      'private',
      'item',
      this.tileInstance.recipe?.dataSource,
    ];
    const rowClickAction = this.tileInstance.openInDrawer ? 'drawer' : 'route';
    const defaultOptions: IItemsTableOptions = {
      columns: this.tileInstance.showColumns?.map((name: string) => {
        return { key: name };
      }),
      defaultPageSize: this.tileInstance.defaultPageSize,
      showPageControls: this.tileInstance.showPageControls,
      showPageSizeOptions: this.tileInstance.showPageSizeOptions,
      rowClickAction,
      itemRoutePrefixes,
      canSort: true,
    };

    this.options = {};

    Object.assign(this.options, defaultOptions);
    Object.assign(this.options, this.tileInstance.tableOptions);
    this.options.columns = this.tileInstance.showColumns?.map((name: string) => ({ key: name })) ?? [];
    this.options.rowClickAction = rowClickAction;

    if (this.editMode) {
      this.allowed = true;
    } else if (this.tileInstance?.recipe?.dataSource) {
      this.allowed = this.productService.isFeatureEnabled('FINDANDPAGINATE', [
        'DATASOURCE:' + this.tileInstance?.recipe?.dataSource,
      ]);
    }

    this.getSubscriptionStatus();

    this.exportDisabled =
      this.editMode ||
      !this.productService.isFeatureEnabled('EXPORT', [
        'DATASOURCE:' + this.filtersChannel?.channelName,
      ]);

    if (this.editMode) {
      this.showExportButton = false;
    }
  }

  private getSubscriptionStatus() {
    this.subscriptionService.currentSubscription
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((currentSubscription) => {
        if (currentSubscription && currentSubscription?.status === 'trialing') {
          this.isTrialing = true;
        }

        if (this.editMode) {
          this.hasRestrictions = false;
        } else {
          this.hasRestrictions = this.productService.hasRestrictions('FINDANDPAGINATE', [
            'DATASOURCE:' + this.tileInstance.recipe?.dataSource,
          ]);
        }
      });
  }

  onTableItemSelected(item: IUIDataSourceItem): void {
    const itemId = String(item?._id ?? '').trim();
    const alias = String(this.dataSourceAlias ?? '').trim();

    if (!itemId || !alias) {
      return;
    }

    this.tileItemDrawerService.open({ alias, itemId });
  }
}
